/**
 * Expresión regular para validar documentos (más o menos). No valida que sea
 * un documento español válido, simplemente que tenga un formato aparente a
 * documento.
 *
 * Esto se usa para los acreditados, que en base de datos hay valores raros que
 * viene de los ficheros de Imserso. Como de todas formas luego únicamente se
 * aceptan los documentos que existen en base de datos (que se corresponden con
 * un acreditado) pues está validación es solo informativa en la web.
 *
 * No se usa para validar DNI, NIE o Pasaporte.
 */
const REGEX_DOCUMENT = /^([a-zA-Z]{0,3}[0-9]+[a-zA-Z]?)$/;

/** Expresión regular para validar direcciones de correo */
/* No valida al 100% pero acepta la mayoía de casos. */
// eslint-disable-next-line max-len
const REGEX_EMAIL = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/** Expresión regular para validar números de teléfono. */
const REGEX_PHONE = /^\d{9}$/;

/** Expresión regular para validar números de teléfono móviles. */
const REGEX_MOBILE_PHONE = /^[67][0-9]{8}$/;

/** Expresión regular para validar texto no númerico */
const TEXT_PATTERN = /^(\u0020*[a-zA-ZáéíóúÁÉÍÓÚàèìòùÀÈÌÒÙäëïöüÄËÏÖÜâêîôûÂÊÎÛÔçÇñÑ0-9.]+\u0020*)+$/;

/**
 * Comprueba si el texto cumple el formato de un documento.
 *
 * @param text el texto a validar.
 */
export function isDocument(text: string): boolean {
  return REGEX_DOCUMENT.test(text);
}

/**
 * Comprueba si el texto cumple el formato de una dirección de email.
 *
 * @param text el texto a validar.
 */
export function isEmail(text: string): boolean {
  return REGEX_EMAIL.test(text);
}

/**
 * Comprueba si el texto cumple el formato de un télefono móvil
 * (9 dígitos empezando por 6 o 7).
 *
 * @param text el texto a validar.
 */
export function isMobilePhone(text: string): boolean {
  return REGEX_MOBILE_PHONE.test(text);
}

/**
 * Comprueba si el texto no esta vacio
 *
 * @param text el texto a validar.
 */
export function isNotEmpty(text: string): boolean {
  return text != null && text.length > 0;
}

/**
 * Comprueba si el texto no esta vacio y tiene algún caracter.
 *
 * @param text el texto a validar.
 */
export function isNotBlank(text: string): boolean {
  return text != null && text.trim().length > 0;
}

/**
 * Comprueba si el texto cumple el formato de un télefono (9 dígitos).
 *
 * @param text el texto a validar.
 */
export function isPhone(text: string): boolean {
  return REGEX_PHONE.test(text);
}

/**
 * Comprueba si el valor es un entero positivo
 *
 * @param text valor a validar.
 */
export function isPositiveInteger(text: string): boolean {
  const value = Number(text);

  return !isNaN(value) && value % 1 === 0 && value >= 0;
}

/**
 * Comprueba si el texto cumple con el formato de letras y números.
 *
 * @param text el texto a validar.
 */
export function isText(text: string): boolean {
  return isNotEmpty(text) && TEXT_PATTERN.test(text);
}
